import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://www.regard-de-nature.fr";

const FleursDeBach = () => {
  useSEO({
    title: "Les fleurs de Bach : équilibre émotionnel naturel – Regard de Nature",
    description:
      "Les fleurs de Bach (élixirs floraux) accompagnent les émotions naturellement : stress, anxiété, transitions de vie. Conseils personnalisés par Laura Elie.",
    canonical: `${SITE}/fleurs-de-bach`,
  });

  return (
    <SeoPageLayout
      h1="Les fleurs de Bach : accompagner vos émotions naturellement"
      intro="Anxiété tenace, ruminations le soir, hypersensibilité, période de transition difficile à traverser ? Les élixirs floraux du Dr Bach offrent un soutien doux, sans effet sédatif, pour retrouver un mouvement intérieur."
    >
      <SeoSection title="Nos émotions, ces compagnes du quotidien">
        <p>
          Je reçois souvent des personnes qui me disent : « je ne suis pas en dépression, mais je n'arrive plus à
          me sentir vraiment en paix ». Peur, tristesse, colère, découragement, anxiété, hypersensibilité — ces
          émotions sont naturelles. Elles deviennent toutefois difficiles à vivre lorsqu'elles s'installent,
          perturbent notre quotidien ou nous empêchent d'avancer.
        </p>
        <p>
          Les fleurs de Bach sont une approche complémentaire qui s'adresse précisément à cette dimension
          émotionnelle. Elles ne soignent pas une maladie : elles offrent un soutien doux pour traverser un état
          émotionnel particulier.
        </p>
      </SeoSection>

      <SeoSection title="Ce qu'elles peuvent vous apporter">
        <SeoList
          items={[
            "Un apaisement progressif des tensions émotionnelles du quotidien.",
            "Plus de recul face aux situations qui vous mettent en difficulté.",
            "Une meilleure traversée des périodes de transition.",
            "Un soutien doux pour les enfants hypersensibles ou en période d'adaptation.",
            "Le sentiment de retrouver un mouvement intérieur, sans masquer ce que vous vivez.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Que sont les fleurs de Bach ?">
        <p>
          Les fleurs de Bach, ou élixirs floraux, ont été élaborées dans les années 1930 par le Dr Edward Bach,
          médecin britannique. Il a sélectionné 38 fleurs sauvages, chacune correspondant à un état émotionnel
          précis. Ces élixirs sont préparés à partir des fleurs et conservés dans une base d'eau et d'alcool.
        </p>
        <p>
          Aujourd'hui, les fleurs de Bach sont reconnues dans le monde entier comme une approche naturelle de
          soutien émotionnel. Elles sont sans toxicité connue aux dosages usuels, et utilisables à tous les âges
          de la vie.
        </p>
        <p className="font-semibold text-primary">
          Important : les fleurs de Bach ne sont pas des médicaments. Elles ne traitent ni ne préviennent aucune
          maladie. Elles s'inscrivent dans une démarche d'accompagnement émotionnel naturel.
        </p>
      </SeoSection>

      <SeoSection title="Dans quels cas peut-on les utiliser ?">
        <SeoList
          items={[
            "Périodes de stress, surcharge, examen, événement important",
            "Anxiété, peurs, ruminations, hypersensibilité",
            "Tristesse, découragement, perte de motivation",
            "Colère, irritabilité, frustration",
            "Transitions de vie : déménagement, séparation, deuil, parentalité",
            "Hypersensibilité émotionnelle de l'enfant ou de l'adulte",
            "Manque de confiance en soi, doute, indécision",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comment je vous accompagne avec les fleurs de Bach">
        <SeoSubSection title="L'écoute">
          <p>
            Tout commence par un temps d'échange. Je prends le temps de vous écouter, de comprendre ce que vous
            vivez actuellement, vos émotions dominantes, vos sensibilités, votre histoire. C'est cette écoute qui
            permet de choisir les bonnes fleurs.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Le choix des élixirs">
          <p>
            Ensemble, nous identifions les états émotionnels qui méritent d'être accompagnés. Je sélectionne
            généralement entre 3 et 7 fleurs, qui forment un mélange personnalisé adapté à votre situation
            actuelle.
          </p>
        </SeoSubSection>
        <SeoSubSection title="L'utilisation">
          <p>
            Le mélange se prend généralement en quelques gouttes plusieurs fois par jour, sur la langue ou dans un
            verre d'eau. La prise dure quelques semaines, le temps que l'état émotionnel évolue naturellement.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Le suivi">
          <p>
            Après quelques semaines, nous faisons un point ensemble. Selon votre évolution, le mélange peut être
            ajusté ou arrêté.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique avec les fleurs de Bach">
        <p>
          J'observe que les élixirs floraux donnent leurs meilleurs résultats quand le mélange est vraiment
          personnalisé — pas un combo générique « anti-stress », mais une composition pensée à partir de ce que
          vous traversez en ce moment précis. C'est pour cela que je prends le temps d'écouter avant de proposer.
        </p>
      </SeoSection>

      <SeoSection title="Les fleurs de Bach et la naturopathie">
        <p>
          Dans mon cabinet, les fleurs de Bach sont l'un des outils que je peux proposer dans le cadre d'un
          accompagnement{" "}
          <Link to="/mon-approche" className="text-primary underline hover:text-secondary">naturopathique</Link>{" "}
          global. Elles s'inscrivent souvent en complément d'autres conseils (alimentation, hygiène de vie, gestion
          du{" "}
          <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">stress</Link>),
          pour une approche holistique de votre bien-être.
        </p>
        <p>
          Il est aussi possible de consulter pour un accompagnement axé spécifiquement sur les fleurs de Bach, sans
          suivi naturopathique global.
        </p>
      </SeoSection>

      <SeoSection title="Questions fréquentes">
        <SeoSubSection title="Combien de temps faut-il pour ressentir un effet ?">
          <p>
            Cela varie d'une personne à l'autre. Certaines ressentent un apaisement dans les premiers jours,
            d'autres après deux à trois semaines. Le mélange se prend généralement sur 3 semaines.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Est-ce adapté aux enfants ?">
          <p>
            Oui, les fleurs de Bach sont adaptées aux enfants (et même aux bébés, sans alcool). Elles peuvent
            soutenir une période d'adaptation, une hypersensibilité, ou une transition familiale.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Y a-t-il des contre-indications ?">
          <p>
            Aux dosages usuels, les fleurs de Bach n'ont pas de contre-indications connues. Pour les personnes
            sensibles à l'alcool (grossesse, sevrage), des versions sans alcool existent.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Pour qui les fleurs de Bach sont-elles adaptées ?">
        <SeoList
          items={[
            "Adultes traversant une période émotionnellement chargée",
            "Parents souhaitant accompagner leur enfant en douceur",
            "Personnes hypersensibles ou en quête d'équilibre intérieur",
            "Toute personne curieuse d'une approche émotionnelle naturelle",
          ]}
        />
        <p className="italic text-foreground/70 mt-4">
          En cas de souffrance psychique importante, de troubles anxieux ou dépressifs, un accompagnement par un
          professionnel de santé mentale (psychologue, psychiatre, médecin) reste indispensable. Les fleurs de Bach
          peuvent venir en complément, mais ne se substituent jamais à un suivi médical ou psychologique.
        </p>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default FleursDeBach;
